import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import { useDispatch, useSelector } from 'react-redux'
import { updateFilter, showFilter } from '../../../../../../../../store/actions/filter.actions'
import { useLocation, useNavigate } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import { updateSkip } from '../../../../../../../../store/actions/datatable.actions'
import DialogConfirmCancel from '../../../../../../../dialog/DialogConfirm'
import FormCurriculumElements from './FormCurriculumElements'
import FormLcElements from './FormLcElements'
import { setChildrenData } from '../../../../../../Assessment/utils'
import { useByStudent } from '../../../ByStudentContext/ByStudentContext'
import { getClasses } from '../../../../../../../../services/classes/classes.service'
import SelectMultipleField from '../../../../../../../FormControl/input/SelectMultipleField'
import { Divider, Typography } from '@mui/material'
import { getDevelopmentalRubrics } from '../../../../../../../../services/developmental-rubrics/developmental-rubrics.service'
import { handlerErrorV2 } from '../../../../../../../../utils/errors'
import InputDateUTC from '../../../../../../../FormControl/input/InputDateUTC'

const FilterDialog = ({ chartType }) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const today = useSelector((state) => state?.date?.date || new Date())
  const { studentSelected, curriculumId } = useByStudent()

  const filter = useSelector((state) => state?.filter.learningMatrix?.filter || {})
  const open = useSelector((state) => state?.filter.learningMatrix?.open || false)

  const dispatch = useDispatch()

  const [form, setInputForm] = useState(filter)
  const [classes, setClasses] = useState({
    data: [],
    loading: false
  })
  const [ul, setUl] = useState({
    data: [],
    loading: false
  })

  useEffect(() => {
    if (!isEmpty(filter)) {
      setInputForm(filter)
    }
  }, [filter])

  const onClose = () => {
    dispatch(showFilter('learningMatrix', false))
  }
  const onLoadClasses = useCallback(async () => {
    if (curriculumId && studentSelected) {
      let data = classes.data || []
      setClasses({ data, loading: true })
      try {
        const response = await getClasses(curriculumId, {
          students: [studentSelected._id]
        })
        if (!response.data || response.error) {
          console.error(response.error)
          return
        }
        data = response.data
      } catch (err) {
        const message = handlerErrorV2(err)
      } finally {
        if (!data.some((cl) => cl._id === 'main')) {
          data.unshift({
            code: 'Main',
            _id: 'main'
          })
        }
        setClasses({ data, loading: false })
      }
    }
  }, [curriculumId, studentSelected, form])

  const onLoadUl = useCallback(async () => {
    if (curriculumId && studentSelected) {
      let data = ul.data || []
      let error = null
      setUl({ data, loading: true, error })
      try {
        const response = await getDevelopmentalRubrics(curriculumId, {
          students: [studentSelected._id],
          classId: form.classId
        })
        if (!response.data || response.error) {
          error = response.error
          return
        }
        data = response.data
      } catch (err) {
        const message = handlerErrorV2(err)
        error = message
      } finally {
        if (!data.some((cl) => cl._id === 'main')) {
          data.unshift({
            name: 'Main',
            _id: 'main'
          })
        }
        setUl({ data, loading: false, error })
      }
    }
  }, [curriculumId, studentSelected, form.classId])

  useEffect(() => {
    onLoadClasses()
  }, [onLoadClasses])
  useEffect(() => {
    onLoadUl()
  }, [onLoadUl])

  const onChange = (field, key, name, value = '') => {
    if (['startDate', 'endDate'].includes(field) && value) {
      setInputForm({ ...form, [field]: value })
    } else if (field === 'ulId') {
      const uls = ul?.data
        ?.filter((cl) => value.includes(cl._id))
        .map((cl) => ({
          name: cl.name,
          value: cl._id
        }))

      setInputForm({ ...form, [field]: value, 'unit of learning': uls })
    } else if (field === 'classId') {
      const cls = classes?.data
        ?.filter((cl) => value.includes(cl._id))
        .map((cl) => ({
          name: cl.code,
          value: cl._id
        }))

      setInputForm({ ...form, [field]: value, classes: cls, ulId: null, 'unit of learning': [] })
    } else if (field === 'curriculumElements') {
      const formData = setChildrenData(form[field] || {}, key, value, true)
      setInputForm({ ...form, [field]: formData })
    } else {
      const formData = form[field] || {}
      const formDataName = form[`${field}_name`] || {}
      if (formData) {
        if (value) {
          formData[key] = value
          formDataName[key] = {
            name,
            value
          }
        } else {
          delete formData[key]
          delete formDataName[key]
        }
      }
      setInputForm({ ...form, [field]: formData, [`${field}_name`]: formDataName })
    }
  }

  const onApply = () => {
    onClose()
    const filter = {
      ...form,
      student: studentSelected
    }

    dispatch(updateFilter('learningMatrix', filter))
    dispatch(updateSkip('learningMatrix', 0))
    navigate(pathname, { state: filter })
  }

  return (
    <DialogConfirmCancel
      id='curriculum-selector-filter'
      open={open}
      title='Filter'
      maxWidth='xs'
      onCancel={onClose}
      onConfirm={onApply}
      textConfirm='Apply'
    >
      <Box className='grid grid-cols-4 gap-4 items-center'>
        <FormCurriculumElements
          form={form.curriculumElements}
          onChange={(key, name, value) => onChange('curriculumElements', key, name, value)}
        />
        <FormLcElements
          form={form.lcElements}
          onChange={(key, name, value) => onChange('lcElements', key, name, value)}
        />
        {['learning-matrix', 'by-unit-of-learning'].includes(chartType) && studentSelected && (
          <>
            <div>
              <Typography
                variant='inherit'
                className='font-bold capitalize'
                color='primary'
                align='right'
                component='p'
              >
                Classes
              </Typography>
            </div>
            <div className='col-span-3'>
              <SelectMultipleField
                name='classId'
                placeholder='Select Class'
                value={form.classId}
                onChange={(e) => onChange('classId', 'classId', 'classId', e.target.value)}
                options={classes.data
                  .filter((item) =>
                    ['by-unit-of-learning'].includes(chartType) ? item._id !== 'main' : true
                  )
                  .map((item) => ({
                    label: item.code,
                    value: item._id
                  }))}
                loading={classes.loading}
                helperText={classes.error}
                error={!!classes.error}
              />
            </div>
            <div>
              <Typography
                variant='inherit'
                className='font-bold capitalize'
                color='primary'
                align='right'
                component='p'
              >
                Unit of Learning
              </Typography>
            </div>
            <div className='col-span-3'>
              <SelectMultipleField
                name='ulId'
                placeholder='Select Unit of Learning'
                value={form.ulId}
                onChange={(e) => onChange('ulId', 'ulId', 'ulId', e.target.value)}
                options={ul.data
                  .filter((item) =>
                    ['by-unit-of-learning'].includes(chartType) ? item._id !== 'main' : true
                  )
                  .map((item) => ({
                    label: item.name,
                    value: item._id
                  }))}
                loading={ul.loading}
                helperText={classes.error}
                error={!!classes.error}
              />
            </div>
          </>
        )}
        {['learning-growth'].includes(chartType) && (
          <>
            <Divider className='col-span-full' />
            <Typography
              variant='inherit'
              className='font-bold capitalize'
              color='primary'
              align='right'
              component='p'
            >
              Start date
            </Typography>
            <div className='col-span-3'>
              <InputDateUTC
                id='dr-start-date'
                name='startDate'
                value={form?.startDate}
                maxDate={form?.endDate || today}
                onChange={(value) => onChange('startDate', 'startDate', 'startDate', value)}
              />
            </div>
            <Typography
              variant='inherit'
              className='font-bold capitalize'
              color='primary'
              align='right'
              component='p'
            >
              End date
            </Typography>
            <div className='col-span-3'>
              <InputDateUTC
                id='dr-end-date'
                name='endDate'
                value={form?.endDate}
                minDate={form?.startDate}
                maxDate={today}
                isStartOfTheDay={false}
                onChange={(value) => onChange('endDate', 'endDate', 'endDate', value)}
              />
            </div>
          </>
        )}
      </Box>
    </DialogConfirmCancel>
  )
}

export default FilterDialog
